import React from 'react';
import PropTypes from 'prop-types';
import ProgressBar from 'react-bootstrap/ProgressBar';
import LoadingIndicator from 'components/LoadingIndicator';
import PlayerCardsPopover from 'components/PlayerCardsPopover';
import ProfileIcon from 'components/ProfileIcon';
import { formatNumber } from 'utils/formatting';
import './ProfilePLSCard.css';

const ProfilePLSCard = ({ user, plsStatus }) => {
  const isLoading = plsStatus?.loading;
  const isError = plsStatus?.error;
  const isPlsPlayerStatusNull = plsStatus?.statusV2?.plsPlayerStatus === null;

  if (isLoading) {
    return <LoadingIndicator />;
  } else if (isError || isPlsPlayerStatusNull) {
    return (
      <div className="alert alert-danger">
        Failed to retrieve PLS Profile. If this continues, please contact
        support.
      </div>
    );
  }

  const {
    plsPlayerId,
    accounts,
    rewardsTier,
    nextRewardTier,
    pointsToUpgradeTier,
    ytdTierPoints,
    firstName,
    lastName
  } = plsStatus?.statusV2?.plsPlayerStatus;

  const plsAccountNumber = accounts[0]?.plsAccountNumber;
  const isFinalTier = rewardsTier === nextRewardTier;
  const tierClass = rewardsTier ? `tier-${rewardsTier.toLowerCase()}` : '';
  const rewardTierMax = pointsToUpgradeTier + ytdTierPoints;
  const profileName =
    firstName && lastName
      ? `${firstName.toLowerCase()} ${lastName.toLowerCase()}`
      : user?.displayName || '';

  const renderTopSection = () => {
    const plsAccountInfo = plsPlayerId ? `Player ID #${plsPlayerId}` : null;

    const rewardsAccountInfo = plsAccountNumber
      ? `Rewards #${plsAccountNumber}`
      : 'Rewards Account Not Registered';

    return (
      <div className="card-top-section">
        <div className="tier-title">{rewardsTier}</div>
        <ProfileIcon
          avatarData={user && user.avatarData}
          size={120}
          border="border"
        />
        <div className="user-info-container">
          <div className="user-name">{profileName}</div>
          <div>{plsAccountInfo}</div>
          <div>{rewardsAccountInfo}</div>
        </div>
        <PlayerCardsPopover accounts={accounts} playerId={plsPlayerId} />
      </div>
    );
  };

  const renderProgressBarPoints = progressPercentage => {
    if (isFinalTier) {
      return <div className="final-tier-ytd-tier-points">{ytdTierPoints}</div>;
    }

    if (progressPercentage === undefined) {
      return null;
    }

    return (
      <div className="progress-bar-points">
        <ProgressBar now={progressPercentage} />
        <div className="points-counter">
          {`${ytdTierPoints} / ${rewardTierMax}`}
        </div>
      </div>
    );
  };

  const renderBottomSection = () => {
    let progressMessage;
    let progressPercentage;

    if (!plsStatus.rewardTier) {
      // no data
      progressMessage = (
        <div>
          Welcome to Wind Creek Casino! Your account is still being processed.
          Please check back soon!
        </div>
      );
    }

    if (isFinalTier) {
      progressMessage = (
        <div className="final-tier">
          You are a <span className="tier-name">{rewardsTier}</span> card
          member, our premier tier with exclusive benefits!
        </div>
      );
    } else {
      progressMessage = (
        <div>
          <div>
            Earn <strong>{formatNumber(pointsToUpgradeTier)} points</strong> to
            upgrade to:
          </div>
          <div className="reward-card-name">
            <span>{nextRewardTier}</span> card
          </div>
        </div>
      );
      // don't use rewardTierMin because only points reset, not level.
      // but minimum for next level is still the same.
      progressPercentage = (100.0 * ytdTierPoints) / rewardTierMax;
    }

    return (
      <div className="card-bottom-section">
        <div className="progress-row">
          <h4 className="progress-ytd-">YTD Tier Points</h4>
          {renderProgressBarPoints(progressPercentage)}
          {progressMessage}
        </div>
      </div>
    );
  };

  return (
    <div className={`pls-profile-card ${tierClass}`}>
      <h2 className="sr-only">Rewards Account Points</h2>
      <div className={'new-profile-tier'}>
        {renderTopSection()}
        {renderBottomSection()}
      </div>
    </div>
  );
};

ProfilePLSCard.propTypes = {
  user: PropTypes.shape({
    avatarData: PropTypes.string,
    displayName: PropTypes.string
  })
  // ADD PLS Data shape
};

export default ProfilePLSCard;
